import { type PlanBuildData, type PlanConfig, type PlanStop, type PlanStopNodeAcquisition, type StopBuilder, ITEMS_PER_MINUTE } from "../plan-template.js";
import { type CurriculumNode } from "../item-library.js";
import { getEligibleNewNodes } from "../scheduler.js";
import { ACQUISITION_THRESHOLDS, DEFAULT_ACQUISITION_THRESHOLD } from "../acquisition.js";
import { nodeToDrillRoute } from "./_helpers.js";

export const introduceStop: StopBuilder = (
  data: PlanBuildData,
  config: PlanConfig,
  alreadyPlanned: ReadonlySet<string>,
): PlanStop | null => {
  const { items, cards, nodes, nodeExposures, forceAcquired, newSkillNodeId, sessionMix, sessionTargetMinutes } = data;
  const { newItemCap } = config;

  if (sessionMix === "review-only") return null;

  const eligible = getEligibleNewNodes(nodes, items, cards);
  if (eligible.length === 0) return null;

  const primaryNode =
    (newSkillNodeId ? eligible.find((n) => n.id === newSkillNodeId) : null) ??
    eligible[0]!;

  const unseen = (nodeId: string) =>
    items.filter(
      (i) =>
        i.nodeId === nodeId &&
        !alreadyPlanned.has(i.id) &&
        (!cards[i.id] || cards[i.id]!.state === "new"),
    );

  // Dynamic cap: absorb unused budget from earlier stops.
  const alreadyUsed = alreadyPlanned.size;
  const remainingMinutes = Math.max(0, sessionTargetMinutes - alreadyUsed / ITEMS_PER_MINUTE);
  const dynamicCap = Math.max(newItemCap, Math.floor(remainingMinutes * ITEMS_PER_MINUTE * 0.9));

  let newItems = unseen(primaryNode.id).slice(0, dynamicCap);

  // Only pair with a same-track second node: guitar exercises use a fretboard
  // UI that the practice session runner can't render, so cross-track pairs break.
  const secondNode = !newSkillNodeId && newItems.length < dynamicCap
    ? eligible.slice(1).find((n) => n.track === primaryNode.track)
    : undefined;
  if (secondNode) {
    const extra = unseen(secondNode.id).slice(0, dynamicCap - newItems.length);
    newItems = [...newItems, ...extra];
  }

  if (newItems.length === 0) return null;

  const getNodeAcquisition = (node: CurriculumNode): PlanStopNodeAcquisition => {
    const exposures = nodeExposures.get(node.id) ?? 0;
    const threshold = ACQUISITION_THRESHOLDS[node.id] ?? DEFAULT_ACQUISITION_THRESHOLD;
    const hasExistingPractice = items.some(
      (i) => i.nodeId === node.id && (cards[i.id]?.reps ?? 0) > 0,
    );
    let status: "needs-listen" | "ready";
    if (forceAcquired || hasExistingPractice) {
      status = "ready";
    } else {
      status = exposures >= threshold ? "ready" : "needs-listen";
    }
    return { nodeId: node.id, status, exposures, threshold };
  };

  const primaryAcquisition = getNodeAcquisition(primaryNode);
  const secondAcquisition = secondNode ? getNodeAcquisition(secondNode) : undefined;
  const nodeAcquisition: PlanStopNodeAcquisition[] = [
    primaryAcquisition,
    ...(secondAcquisition ? [secondAcquisition] : []),
  ];

  const listenFirst = primaryAcquisition.status === "needs-listen";
  const trackLabel =
    primaryNode.track.charAt(0).toUpperCase() + primaryNode.track.slice(1);
  const reason = secondNode
    ? `Continuing into ${secondNode.title} — both in your ${trackLabel} track.`
    : `Next in your ${trackLabel} track. All prerequisites met.`;

  return {
    kind: "introduce",
    title: `Introducing: ${primaryNode.title}`,
    reason,
    itemCount: newItems.length,
    estimatedMinutes: Math.max(1, Math.round(newItems.length / ITEMS_PER_MINUTE)),
    items: newItems,
    nodeAcquisition,
    ...(listenFirst ? { listenFirst: true, drillRoute: nodeToDrillRoute(primaryNode.id) } : {}),
  };
};
